import _ from 'lodash';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';

import { captureException } from '@playbookux/playbook-web-core';
import { UPDATE_STUDY_STATUS, REPROCESS_PROJECT_ELASTIC_SEARCH, UPDATE_RECORDER_TYPE } from './graphql';
import { STATUS, STATUS_INDEXED } from './constants';

import Modal from './modal';

import {
  Container,
  HeldButton,
  Radio,
  RadioContainer,
  RecorderContent,
  ReleaseButton,
  Wrapper,
  Text,
  ErrorMessage,
  WrapperContent,
  Link,
} from './styles';
import { formatDateWithEstTimezone } from '../../../../../core/utils/format-date';

const recorderTypes = [
  {
    key: 'chrome_extension',
    label: 'Chrome Extension',
  },
  {
    key: 'desktop_app',
    label: 'Desktop App',
  },
];

const Status = (props) => {
  const { project, refetch } = props;
  const {
    id,
    studyStatus,
    testType,
    submittedAvailabilities,
    elasticsearchProjectStatus,
    indexedOn,
    participantsSource,
    segments,
  } = project;

  const isUnmoderated = testType === 'unmoderated';

  const [showModal, setShowModal] = useState(false);

  const [updateStudyStatus, { loading }] = useMutation(UPDATE_STUDY_STATUS, {
    onCompleted: refetch,
  });
  const [reprocessProjectElasticsearch] = useMutation(REPROCESS_PROJECT_ELASTIC_SEARCH, {
    onCompleted: refetch,
  });
  const [updateRecorderType] = useMutation(UPDATE_RECORDER_TYPE, {
    onCompleted: refetch,
  });

  const hasComputerStudy = segments.some((segment) => segment.device === 'computer');

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleConfirm = async () => {
    try {
      await updateStudyStatus({
        variables: { id },
      });
      handleCloseModal();
    } catch (err) {
      captureException('UPDATE_STUDY_STATUS_FAILURE', err);
    }
  };

  const handleReprocess = async () => {
    try {
      await reprocessProjectElasticsearch({
        variables: { id },
      });
    } catch (err) {
      captureException('REPROCESS_PROJECT_ELASTIC_SEARCH_FAILURE', err);
    }
  };

  const isModeratedProjectNotReady = () => testType === 'moderated' && !submittedAvailabilities;

  const handleClick = () => {
    setShowModal(true);
  };

  const handleChangeRecorderType = async (value) => {
    try {
      await updateRecorderType({
        variables: { id, recorderType: value },
      });
    } catch (err) {
      captureException('UPDATE_RECORD_TYPE_FAILURE', err);
    }
  };

  return (
    <Container>
      <Modal
        onConfirm={handleConfirm}
        onClose={handleCloseModal}
        open={showModal}
        loading={loading}
        status={studyStatus}
      />
      {participantsSource === 'playbook' && (
        <>
          <Text.Bold>{`Status: ${_.capitalize(studyStatus)}`}</Text.Bold>
          {studyStatus === STATUS.HELD ? (
            <Wrapper>
              <ReleaseButton
                onClick={handleClick}
                loading={loading}
                disabled={loading || isModeratedProjectNotReady()}
              >
                Release to tester platform
              </ReleaseButton>
              {isModeratedProjectNotReady() && (
                <ErrorMessage>
                  No calendar times available
                </ErrorMessage>
              )}
            </Wrapper>
          ) : (
            <>
              <HeldButton
                onClick={handleClick}
                loading={loading}
                disabled={loading}
              >
                Hold Study
              </HeldButton>
              <WrapperContent>
                <Text.Bold>
                  {`Indexed on ${indexedOn ? formatDateWithEstTimezone(indexedOn) : ''}`}
                </Text.Bold>
                {elasticsearchProjectStatus === STATUS_INDEXED.SUCCESS ? (
                  <Text.Success>{`Indexing ${(elasticsearchProjectStatus)}`}</Text.Success>
                ) : (
                  <Text.Error>{`Indexing ${(elasticsearchProjectStatus)}`}</Text.Error>
                )}

                <Link onClick={handleReprocess}>
                  Reprocess
                </Link>
              </WrapperContent>
            </>
          )}
        </>
      )}

      {isUnmoderated && hasComputerStudy && (
        <RecorderContent>
          <Text.Header>Recorder Type</Text.Header>

          {recorderTypes.map((type) => {
            const checked = type.key === segments[0]?.recorderType;

            return (
              <RadioContainer key={type.key} onClick={() => !checked && handleChangeRecorderType(type.key)}>
                <Radio
                  value={type.key}
                  checked={checked}
                />

                <Text
                  fontSize="14px"
                >
                  {type.label}
                </Text>
              </RadioContainer>
            );
          })}
        </RecorderContent>
      )}
    </Container>
  );
};

Status.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string,
    participantsSource: PropTypes.string,
    submittedAvailabilities: PropTypes.bool,
    testType: PropTypes.string,
    studyStatus: PropTypes.string,
    releasedAt: PropTypes.string,
    elasticsearchProjectStatus: PropTypes.string,
    indexedOn: PropTypes.string,
    segments: PropTypes.arrayOf(PropTypes.shape({
      recorderType: PropTypes.string,
    })),
    company: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  }).isRequired,
  refetch: PropTypes.func.isRequired,
};

export default Status;
